import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal typings for the Web Speech API, which is not in the standard DOM lib.
interface SpeechRecognitionAlternative { transcript: string }
interface SpeechRecognitionResult { 0: SpeechRecognitionAlternative; isFinal: boolean; length: number }
interface SpeechRecognitionResultList { length: number; [index: number]: SpeechRecognitionResult }
interface SpeechRecognitionEventLike { resultIndex: number; results: SpeechRecognitionResultList }
interface SpeechRecognitionErrorEventLike { error: string }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

// iOS Safari (incl. standalone PWAs) hangs / restart-loops when continuous mode
// is enabled, so we run single-utterance there. Covers iPadOS-as-desktop too.
function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
}

export interface Dictation {
  supported: boolean;
  listening: boolean;
  error: string | null;
  // Begins a dictation session. `baseText` is preserved; recognized speech is
  // appended to it and streamed back through onUpdate as the user speaks.
  start: (baseText: string) => void;
  stop: () => void;
  toggle: (baseText: string) => void;
}

// Live speech-to-text built on the browser's SpeechRecognition. No backend:
// recognition happens on-device / via the browser vendor's service.
export function useDictation(onUpdate: (text: string) => void): Dictation {
  const [supported] = useState<boolean>(() => getRecognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const baseRef = useRef('');
  const finalRef = useRef('');
  // Keep the latest callback without forcing start/stop to be re-created.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec) {
      try { rec.stop(); } catch { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const start = useCallback((baseText: string) => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) { setError('Voice input is not supported in this browser.'); return; }
    // Tear down any prior session before starting a fresh one.
    if (recRef.current) { try { recRef.current.abort(); } catch { /* noop */ } }

    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = !isIOS();
    rec.interimResults = true;

    const trimmed = baseText.replace(/\s+$/, '');
    baseRef.current = trimmed ? trimmed + ' ' : '';
    finalRef.current = '';

    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res[0].transcript;
        if (res.isFinal) finalRef.current += text;
        else interim += text;
      }
      onUpdateRef.current(baseRef.current + finalRef.current + interim);
    };
    rec.onerror = (e) => {
      // Silence/aborts are normal lifecycle events, not user-facing errors.
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError('Microphone access was blocked. Allow it in your browser settings.');
      } else {
        setError('Voice input failed. Please try again.');
      }
      setListening(false);
    };
    rec.onstart = () => { setListening(true); };
    rec.onend = () => { setListening(false); };

    recRef.current = rec;
    setError(null);
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() throws if called while already running; ignore.
    }
  }, []);

  const toggle = useCallback((baseText: string) => {
    if (listening) stop(); else start(baseText);
  }, [listening, start, stop]);

  // Stop recognition if the component unmounts mid-session.
  useEffect(() => () => { if (recRef.current) { try { recRef.current.abort(); } catch { /* noop */ } } }, []);

  return { supported, listening, error, start, stop, toggle };
}
